﻿import type {
	NestedLayoutUmbBlockElementModel,
	NestedLayoutUmbBlockSettingsElementModel,
} from "#/_generated/umbraco/delivery-api/models";
import { useContext } from "react";
import { BlockPreviewContext } from "#/_packages/umbrella/features/previews/block-preview-context";
import {
	NestedLayout,
	type NestedLayoutAlignItems,
	type NestedLayoutBoxVariant,
	type NestedLayoutDisplay,
	type NestedLayoutFlexDirection,
	type NestedLayoutFlexWrap,
	type NestedLayoutJustifyContent,
	type NestedLayoutRounded,
} from "#/_packages/umbrella/features/nested-layout/components";
import type { BlockItemProps } from "#/_packages/umbrella/integrations/umbraco/types";
import { toHoverItemCssClasses } from "#/_packages/umbrella/integrations/umbraco/utils/animation-utils";
import {
	tryGetAlignment,
	tryGetAnimationScrollType,
	tryGetHorizontalAlignment,
} from "#/_packages/umbrella/utils";

type NestedLayoutBlockProps = BlockItemProps & {
	content: NestedLayoutUmbBlockElementModel;
	settings: NestedLayoutUmbBlockSettingsElementModel;
};

export default function NestedLayoutUmbBlock({
	className,
	content,
	settings,
}: NestedLayoutBlockProps) {
	const isPreview = useContext(BlockPreviewContext);
	const animationScrollType = tryGetAnimationScrollType(
		settings.properties?.animationScrollType,
	);

	return (
		<NestedLayout
			id={settings.properties?.generatedId ?? ""}
			className={className}
			style={
				isPreview
					? {
							border:
								"1px dashed var(--uui-button-border-color, var(--uui-color-border-standalone, #c2c2c2))",
							borderRadius:
								"var(--uui-button-border-radius, var(--uui-border-radius, 3px))",
						}
					: undefined
			}
			display={
				(settings.properties?.cssDisplay as NestedLayoutDisplay) ?? undefined
			}
			flexWrap={
				(settings.properties?.cssFlexWrap as NestedLayoutFlexWrap) ?? undefined
			}
			flexDirection={
				(settings.properties?.cssFlexDirection as NestedLayoutFlexDirection) ??
				undefined
			}
			justifyContent={
				(settings.properties
					?.cssJustifyContent as NestedLayoutJustifyContent) ?? undefined
			}
			alignItems={
				(settings.properties?.cssAlignItems as NestedLayoutAlignItems) ??
				undefined
			}
			alignmentHorizontal={tryGetHorizontalAlignment(
				settings.properties?.alignmentHorizontal,
			)}
			alignmentSelfVertical={tryGetAlignment(
				settings.properties?.alignmentSelfVertical,
			)}
			columnGap={settings.properties?.cssColumnGap ?? undefined}
			rowGap={settings.properties?.cssRowGap ?? undefined}
			rounded={
				(settings.properties?.rounded as NestedLayoutRounded) ?? undefined
			}
			boxVariant={
				(settings.properties?.boxVariant as NestedLayoutBoxVariant) ?? undefined
			}
			hoverItemClasses={toHoverItemCssClasses(
				settings.properties?.animationHoverType,
			)}
			animationScrollType={
				animationScrollType !== "none" ? animationScrollType : undefined
			}
			blockGrid={
				content.properties?.blockGrid ??
				(isPreview ? { gridColumns: 12, items: [] } : undefined)
			}
		/>
	);
}
